import WorkspaceMembership from "./workspace-membership";
import {
    setWorkspace,
    setWorkspaceMembership,
    setWorkspaceMemberships,
    setWorkspaceSelectionRequired,
} from "../../system/app-slice";
import {useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {normalizeApiError} from "../../system/api";
import {
    collectWorkspaceTasks,
    hydrateWorkspaceSnapshot,
    setWorkspaceGraphMembership,
    setWorkspaceGraphMemberships,
    setWorkspaceGraphWorkspaceId,
    resetWorkspaceGraph,
} from "../../system/workspace-graph-slice";
import {normalizeWorkspaceIdentityGraph} from "./workspace-membership-identity";
import {
    buildActiveWorkspaceContextQuery,
    buildWorkspaceMembershipListQuery,
} from "./workspace-membership-query";
import {resolveWorkspaceSelection} from "./workspace-membership-availability";

function normalizeMembership(membership) {
    const workspaceMemberships = membership?.workspace?.workspace_memberships || [];
    return {
        ...membership,
        workspace: normalizeWorkspaceIdentityGraph(
            membership?.workspace,
            workspaceMemberships,
        ),
    };
}

export default function useWorkspaceMembership() {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState(null);
    const {user} = useSelector((state) => state.auth);
    const {workspaceMembershipData, workspaceMembershipsData} = useSelector((state) => state.app);
    const dispatch = useDispatch();

    const getWorkspaceMemberships = async () => {
        setLoading(true);
        setStatus("loading");
        setError(null);
        const workspaceMembership = new WorkspaceMembership();

        try {
            const listResponse = await workspaceMembership.getAll(
                buildWorkspaceMembershipListQuery(user.id),
            );
            const rawMemberships = Array.isArray(listResponse?.data)
                ? listResponse.data
                : [];
            const selection = resolveWorkspaceSelection(
                rawMemberships.map(normalizeMembership),
                workspaceMembershipData,
                listResponse?.meta?.workspaceSelectionRequired,
            );
            let memberships = selection.memberships;
            const selectionRequired = selection.selectionRequired;
            let defaultMembership = selection.defaultMembership;

            if (defaultMembership?.documentId || defaultMembership?.id) {
                const activeResponse = await workspaceMembership.getById(
                    defaultMembership.documentId || defaultMembership.id,
                    buildActiveWorkspaceContextQuery(user.id),
                );
                const activeMembership = activeResponse?.data;
                if (!activeMembership?.workspace) {
                    throw new Error("workspace-bootstrap.invalid-active-membership");
                }

                defaultMembership = normalizeMembership({
                    ...defaultMembership,
                    ...activeMembership,
                });
                defaultMembership = {
                    ...defaultMembership,
                    workspace: {
                        ...defaultMembership.workspace,
                        tasks: [],
                        customers: [],
                    },
                };

                const activeKey = String(
                    defaultMembership.documentId || defaultMembership.id,
                );
                memberships = memberships.map((membership) => (
                    String(membership.documentId || membership.id) === activeKey
                        ? defaultMembership
                        : membership
                ));
            }

            dispatch(setWorkspaceMemberships(memberships));
            dispatch(setWorkspaceMembership(defaultMembership));
            dispatch(setWorkspaceSelectionRequired(selectionRequired));
            dispatch(setWorkspaceGraphMemberships(memberships));
            dispatch(setWorkspaceGraphMembership(defaultMembership));

            if (defaultMembership) {
                const workspace = defaultMembership.workspace;
                // Reloading the same workspace (e.g. a realtime membership change) must not replace the task list:
                // this snapshot only carries the partial task data nested in projects, and the tasks pages filter
                // the shared list by assignee, so replacing it emptied the board. Tasks are replaced only when the
                // active workspace changes; otherwise they stay current through task-level updates.
                const sameWorkspace = Boolean(workspace?.documentId)
                    && workspace.documentId === workspaceMembershipData?.workspace?.documentId;
                dispatch(setWorkspace(workspace?.documentId));
                dispatch(setWorkspaceGraphWorkspaceId(workspace?.documentId));
                dispatch(hydrateWorkspaceSnapshot({
                    workspaceId: workspace?.documentId,
                    workspaceMembership: defaultMembership,
                    workspaceMemberships: memberships,
                    goals: workspace?.goals || [],
                    projects: workspace?.projects || [],
                    teams: workspace?.teams || [],
                    customers: workspace?.customers || [],
                    tasks: sameWorkspace ? undefined : collectWorkspaceTasks(workspace),
                }));
            } else if (selectionRequired) {
                dispatch(setWorkspace(null));
                dispatch(resetWorkspaceGraph(memberships));
            }

            setLoading(false);
            setStatus("succeeded");
            return {
                ...listResponse,
                data: memberships,
            };
        } catch (requestError) {
            setLoading(false);
            setStatus("failed");
            setError(requestError?.normalizedError || normalizeApiError(requestError));
            return null;
        }
    };

    const updateDefaultWorkspaceMembership = async (documentId) => {
        const workspaceMembership = new WorkspaceMembership();
        try {
            await workspaceMembership.updateItem(documentId, {isDefault: true});
            return await getWorkspaceMemberships();
        } catch (requestError) {
            return null;
        }
    };

    const deleteWorkspaceMemberShip = async (documentId) => {
        const workspaceMembership = new WorkspaceMembership();
        try {
            await workspaceMembership.deleteItem(documentId);
            return await getWorkspaceMemberships();
        } catch (requestError) {
            return null;
        }
    };

    const updateWorkspaceMembershipStatus = async (documentId, memberShipStatus) => {
        if (!documentId || !["joined", "restricted"].includes(memberShipStatus)) return null;

        setLoading(true);
        setStatus("loading");
        setError(null);
        const workspaceMembership = new WorkspaceMembership();
        try {
            const updated = await workspaceMembership.updateItem(
                documentId,
                {memberShipStatus},
                workspaceMembershipData?.workspace?.documentId,
            );
            await getWorkspaceMemberships();
            return updated?.data || updated;
        } catch (requestError) {
            setLoading(false);
            setStatus("failed");
            setError(requestError?.normalizedError || normalizeApiError(requestError));
            throw requestError;
        }
    };

    const updateWorkspaceConfigs = async (workspaceConfigs) => {
        const documentId = workspaceMembershipData?.documentId || workspaceMembershipData?.id;
        if (!documentId || !workspaceConfigs || typeof workspaceConfigs !== "object") return null;

        const workspaceMembership = new WorkspaceMembership();
        try {
            const response = await workspaceMembership.updateItem(documentId, {workspaceConfigs});
            const updatedData = response?.data || response || {};
            const nextMembership = normalizeMembership({
                ...workspaceMembershipData,
                ...updatedData,
                workspace: workspaceMembershipData?.workspace,
                workspaceConfigs,
            });
            const membershipKey = String(documentId);
            const nextMemberships = (workspaceMembershipsData || []).map((membership) => (
                String(membership?.documentId || membership?.id || "") === membershipKey
                    ? nextMembership
                    : membership
            ));
            dispatch(setWorkspaceMembership(nextMembership));
            dispatch(setWorkspaceMemberships(nextMemberships));
            dispatch(setWorkspaceGraphMembership(nextMembership));
            dispatch(setWorkspaceGraphMemberships(nextMemberships));
            return nextMembership;
        } catch (requestError) {
            setError(requestError?.normalizedError || normalizeApiError(requestError));
            return null;
        }
    };

    return {
        getWorkspaceMemberships,
        updateDefaultWorkspaceMembership,
        updateWorkspaceMembershipStatus,
        updateWorkspaceConfigs,
        deleteWorkspaceMemberShip,
        loading,
        status,
        error,
    };
}
